import { getDocLang, getDocLocale } from "Helper/metaHelper";

/**
 * returns the offset of the day starting the locale week with sunday (the default week start)
 * @returns {0|1}
 */
export function getLocaleWeekStartOffset() {
  // currently FRA supports 3 locales, eng, chinese and german.
  // only the german week starts with monday, others with sunday
  if (getDocLang().toLocaleLowerCase().startsWith("de")) {
    return 1;
  }
  return 0;
}

/**
 * returns formatted day, month, day... values of the given date in a json.
 *
 * @param {Date|string} date
 * @returns {Object}
 */
export function toParts(date: Date | string) {
  if (typeof date === "string") date = new Date(date);
  return {
    // e.g. "08"
    second: String(date.getSeconds()).padStart(2, "0"),
    minute: String(date.getMinutes()).padStart(2, "0"),
    hour: String(date.getHours()).padStart(2, "0"),
    day: String(date.getDate()).padStart(2, "0"),
    // month is zero based
    month: String(date.getMonth() + 1).padStart(2, "0"),
    year: String(date.getFullYear()),
  };
}

/**
 * convert date to "YYYY-MM-DD" format
 * @example
 *  FraDatepicker.toDateString(new Date()) === "2020-02-29"
 *
 * @export
 * @param {Date | null | string} date
 * @returns {string}
 */
export function toDateString(date: Date | null | string): string {
  if (date === null || date === "") return "";

  const { year, month, day } = toParts(date);
  return `${year}-${month}-${day}`;
}

/**
 * returns a localized representing of the given date
 * @example FraDatepicker.toLocaleDateString(new Date(), true) === "02.04.2022, 11:00"
 *
 * @param {Date | null | string} date - date which gets localized
 * @param {boolean} [withTime=false] - should the time part of the date also be part of the return text
 * @returns {string}
 */
export function toLocaleDateString(date: Date | null | string, withTime = false): string {
  if (date === null || date === "") return "";

  const formatOption: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  };

  if (withTime) {
    Object.assign(formatOption, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  }

  const intlFormatter = new Intl.DateTimeFormat(getDocLocale(), formatOption);

  return intlFormatter.format(new Date(date));
}

/**
 * convert date to "YYYY-MM-DD" format
 * @example
 *  FraDatepicker.toDateString(new Date()) === "2020-02-29"
 *
 * @export
 * @param {Date | null} date
 * @returns {string}
 */
export function toTimeString(date: Date | null | string): string {
  if (date === null || date === "") return "";

  const { hour, minute } = toParts(date);
  return `${hour}:${minute}`;
}

/**
 * convert date to "YYYY-MM-DDTmm:ss" format
 * @example
 *  FraDatepicker.toDateTimeString(new Date()) === "2020-02-29T08:35"
 *
 * @export
 * @param {Date | null} date
 * @returns {string}
 */
export function toDateTimeString(date: Date | null | string): string {
  if (date === null || date === "") return "";

  const { year, month, day, hour, minute } = toParts(date);
  return `${year}-${month}-${day}T${hour}:${minute}`;
}

/**
 * Generates week day names in the browser's default locale, starting on Sunday/Monday depending on locale
 *
 * @returns {string[]} - week days' narrow name
 */
export function getDayNames() {
  const dayNames = [];
  const date = new Date();
  const daysToNextWeekStart = 7 - date.getDay() + getLocaleWeekStartOffset();
  date.setDate(date.getDate() + daysToNextWeekStart);
  for (let i = 0; i < 7; i++) {
    dayNames.push({
      name: date.toLocaleString(getDocLang(), { weekday: "long" }),
      abbr: date.toLocaleString(getDocLang(), {
        // for EN and chinese the short weekday names are too bing for the layout. (e.g. Sun vs So in german)
        weekday: getDocLang().startsWith("de") ? "short" : "narrow",
      }),
    });
    // advance the day
    date.setDate(date.getDate() + 1);
  }

  return dayNames;
}
